import React, { Suspense, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Tabs, Tab, Link, Tooltip, Autocomplete, Chip, TextField, Typography } from "@mui/material";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import styles from "../index.module.css";
import CustomTextField from "../../CustomTextField";
import TextArea from "../../TextArea";
import PromoStoreDescriptionEditor from "../PromoStoreDescriptionEditor";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import FilesPopup from "../FilesPopup";
import PromoStoreProductGalleryPreview from "../PromoStoreProductGalleryPreview";
import {
  normalizePromoStoreImageSelection,
  collectEditItemImageUrls,
  toAsiNormalImageUrl,
} from "../asiImageUtils";
import { resolveSupplierCurrencyCode } from "../supplierCurrency";

const AsiImageGallery = React.lazy(() => import("../AsiImageGallery"));

const ItemDetails = ({
  formData,
  onChange,
  orderItem,
  asiLiveRawData,
  handleDone,
  clientFiles,
  customerId,
  productIdUiLabel,
  productIdUiValue,
  isPromoStore = false,
  promoStoreCategories = [],
  selectedCategoryIds = [],
  onChangePromoStoreCategories,
  productResourceErrors = {},
  onAddProductResource,
  onRemoveProductResource,
  onChangeProductResource,
}) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [promoStoreGalleryOpen, setPromoStoreGalleryOpen] = useState(false);
  const [variantTabIndex, setVariantTabIndex] = useState(0);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    onChange(name, value);
  };

  const handleTextAreaChange = (event, name) => {
    const { value } = event.target;
    onChange(name, value);
  };

  const handleImageClick = () => setPopupOpen(true);
  const handleClosePopup = () => setPopupOpen(false);

  const clientFilesArray = clientFiles?.map((file) => ({
    filePath: file?.filePath || file?.url || file?.path || "",
    fileName: file?.fileName || file?.name || "",
    folder: file?.folder || file?.folderName || "",
    status: file?.status || file?.meta?.status || "",
    createdAt: file?.createdAt || file?.uploadedAt || file?.updatedAt || "",
  })) || [];

  const itemImageUrls = useMemo(
    () => collectEditItemImageUrls(orderItem, asiLiveRawData),
    [orderItem, asiLiveRawData]
  );

  const itemUrls = useMemo(
    () => itemImageUrls.map((filePath) => ({ filePath })),
    [itemImageUrls]
  );

  const imageGroups = {
    "Item Files": [...itemUrls],
    "Client Files": [...clientFilesArray],
  };

  const promoStoreGallerySelection = useMemo(
    () => normalizePromoStoreImageSelection({
      selectedImage: formData?.selectedImage,
      imageUrls: formData?.imageUrls,
    }),
    [formData?.selectedImage, formData?.imageUrls]
  );

  const imageChange = (img) => {
    onChange("selectedImage", img);
  };

  const handlePromoStoreImagesChange = (images) => {
    const next = normalizePromoStoreImageSelection({
      imageUrls: images,
    });
    onChange("imageUrls", next.imageUrls);
    onChange("selectedImage", next.selectedImage);
  };

  const handleVariantTabChange = (_, newValue) => {
    setVariantTabIndex(newValue);
  };

  const currencyCode = resolveSupplierCurrencyCode({ asiLiveRawData, orderItem });

  const variants = asiLiveRawData?.Variants || [];

  const flagUrl = (countryCode) =>
    `https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`;

  const heroImageSrc = toAsiNormalImageUrl(
    formData?.selectedImage || itemImageUrls[0] || ""
  );
  const productResources = Array.isArray(formData?.productResources) ? formData.productResources : [];
  const selectedCategories = useMemo(() => {
    const categoriesById = new Map(
      (Array.isArray(promoStoreCategories) ? promoStoreCategories : []).map((category) => [
        String(category?.id),
        category,
      ]),
    );
    const seen = new Set();
    return (Array.isArray(selectedCategoryIds) ? selectedCategoryIds : [])
      .map((categoryId) => categoriesById.get(String(categoryId)) || null)
      .filter((category) => {
        if (!category?.id || seen.has(String(category.id))) return false;
        seen.add(String(category.id));
        return true;
      });
  }, [promoStoreCategories, selectedCategoryIds]);

  const categoryFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f3f4f6",
      borderRadius: "8px",
      minHeight: 48,
      alignItems: "center",
      padding: "7px 40px 7px 10px",
      gap: "6px",
      "& fieldset": {
        borderColor: "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#c4cbd4",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#9ca3af",
        borderWidth: 1,
      },
    },
    "& .MuiAutocomplete-inputRoot": {
      alignItems: "center",
      padding: "0 !important",
    },
    "& .MuiInputBase-input": {
      fontSize: "0.95rem",
      color: "#1f2937",
      minWidth: 96,
      padding: "2px 0 !important",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#6b7280",
      opacity: 1,
    },
    "& .MuiAutocomplete-tag": {
      margin: "0 !important",
    },
    "& .MuiChip-root": {
      height: 24,
      borderRadius: "999px",
      backgroundColor: "#ffffff",
      border: "1px solid #d1d5db",
      color: "#334155",
      fontWeight: 600,
    },
    "& .MuiChip-label": {
      paddingLeft: "10px",
      paddingRight: "4px",
      fontSize: "0.78rem",
    },
    "& .MuiChip-deleteIcon": {
      color: "#64748b !important",
      fontSize: 16,
      margin: "0 6px 0 0",
    },
    "& .MuiAutocomplete-endAdornment": {
      right: 10,
      top: "calc(50% - 14px)",
    },
    "& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator": {
      color: "#64748b",
      padding: 2,
    },
  };

  return (
    <Box className={styles.productDetailsContainer}>
      <div className={`${styles["order-item-edit-fields"]} order-edit-product-main-details`}>
        <Box className="product-popup-field-container">
          <CustomTextField
            label="Product Name"
            isRequired={true}
            name="productName"
            placeholder="Enter product name"
            handleChange={handleFieldChange}
            value={formData.productName}
          />
        </Box>
        <Box className="product-popup-field-container">
          <CustomTextField
            label="Supplier"
            isRequired={true}
            name="supplier"
            placeholder="Enter supplier name"
            handleChange={handleFieldChange}
            value={formData.supplier}
          />
        </Box>
        <Box className="product-popup-field-container">
          <CustomTextField
            isRequired={true}
            name="asiId"
            placeholder="Enter ASI ID"
            handleChange={handleFieldChange}
            label={productIdUiLabel}
            value={productIdUiValue}
            disabled={true}
          />
        </Box>
        <Box className="product-popup-field-container introduction-field">
          <TextArea
            isRequired={true}
            name={isPromoStore ? "privateNote" : "description"}
            label={isPromoStore ? "Short Description" : "Product Description"}
            rows={isPromoStore ? 2 : undefined}
            value={isPromoStore ? formData.privateNote : formData.description}
            handleInputChange={handleTextAreaChange}
          />
        </Box>
        {isPromoStore && (
          <Box className="product-popup-field-container introduction-field">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
              <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                Product Resources
              </span>
              <Tooltip title="Add links to supplier resources such as size charts, spec sheets, care guides, or product documents.">
                <InfoOutlinedIcon
                  aria-label="Product Resources help"
                  sx={{ fontSize: 18, color: "#64748b", cursor: "help" }}
                />
              </Tooltip>
            </Box>
            {productResources.map((resource, index) => {
              const resourceKey = String(resource.id || resource.tempResourceId || index);
              const resourceErrors = productResourceErrors?.[resourceKey] || {};
              const normalizedUrl = typeof resource?.url === "string" ? resource.url.trim() : "";
              const isUrlValid = !resourceErrors?.url && Boolean(normalizedUrl);

              return (
                <Box
                  key={resourceKey}
                  sx={{ borderTop: "1px solid #e5e7eb", pt: 2, mt: index === 0 ? 0 : 2 }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Resource {index + 1}</div>
                  <CustomTextField
                    name={`productResourceName-${resourceKey}`}
                    label="Resource Name"
                    placeholder="Size Chart"
                    handleChange={(event) => onChangeProductResource?.(resourceKey, "name", event.target.value)}
                    value={resource.name || ""}
                    helperText={resourceErrors?.name || ""}
                    error={Boolean(resourceErrors?.name)}
                  />
                  <CustomTextField
                    name={`productResourceUrl-${resourceKey}`}
                    label="Resource URL"
                    placeholder="https://supplier.com/product/resource"
                    handleChange={(event) => onChangeProductResource?.(resourceKey, "url", event.target.value)}
                    value={resource.url || ""}
                    helperText={resourceErrors?.url || ""}
                    error={Boolean(resourceErrors?.url)}
                    type="url"
                  />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                    {isUrlValid ? (
                      <Link
                        href={normalizedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ display: "inline-flex", fontWeight: 700 }}
                      >
                        Open Resource ↗
                      </Link>
                    ) : <span />}
                    <Button
                      type="button"
                      color="error"
                      onClick={() => onRemoveProductResource?.(resourceKey)}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              );
            })}
            {productResources.length < 5 ? (
              <Button
                type="button"
                onClick={() => onAddProductResource?.()}
                sx={{ textTransform: "none", fontWeight: 700, mt: 1, pl: 0 }}
              >
                + Add Resource
              </Button>
            ) : (
              <p style={{ margin: "12px 0 0" }}>Maximum of 5 product resources.</p>
            )}
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#555454", mb: 0.5 }}>
                Categories
              </Typography>
              <Autocomplete
                multiple
                size="small"
                options={promoStoreCategories}
                value={selectedCategories}
                onChange={(_event, nextValue) => onChangePromoStoreCategories?.(nextValue)}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                disableCloseOnSelect
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={selectedCategories.length ? "" : "Select categories"}
                    sx={categoryFieldSx}
                  />
                )}
              />
            </Box>
          </Box>
        )}
        <Box className="product-popup-field-container introduction-field">
          {isPromoStore ? (
            <PromoStoreDescriptionEditor
              value={formData.description}
              onChange={(html) => onChange("description", html)}
            />
          ) : (
            <TextArea
              isRequired={true}
              name="privateNote"
              label="Private Note"
              rows={2}
              handleInputChange={handleTextAreaChange}
              value={formData.privateNote}
            />
          )}
        </Box>

        <div className="product-popup-field-container introduction-field MuiBox-root product-popup-variant-container">
          {variants.length > 0 && (
            <div className="product-popup-field-container product-popup-variant-container-inner">
              <div
                style={{
                  color: "#b22",
                  fontStyle: "italic",
                  marginBottom: "12px",
                }}
              >
                Please note, more than {variants.length} variants exist for
                this product. Please ensure you review carefully to apply the
                correct pricing.
              </div>

              <Tabs
                value={variantTabIndex}
                onChange={handleVariantTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
              >
                {variants.map((variant, idx) => {
                  const label = variant.Name
                    ? variant.Name.split(",")[0]
                    : `Variant ${idx + 1}`;
                  return <Tab key={idx} label={label} />;
                })}
              </Tabs>

              {variants.map((variant, idx) => (
                <div
                  key={idx}
                  role="tabpanel"
                  hidden={variantTabIndex !== idx}
                  id={`variant-tabpanel-${idx}`}
                  aria-labelledby={`variant-tab-${idx}`}
                  style={{
                    padding: "12px",
                    backgroundColor: "#fff",
                    borderRadius: 4,
                    border: "1px solid #ddd",
                    marginBottom: "16px",
                  }}
                >
                  {variantTabIndex === idx && (
                    <div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Variant #{idx + 1} Details</strong>
                      </div>

                      <p>
                        <strong>Id:</strong> {variant.Id}
                      </p>
                      <p>
                        <strong>Name:</strong> {variant.Name}
                      </p>
                      <p>
                        <strong>Description:</strong> {variant.Description}
                      </p>
                      <p>
                        <strong>ImageUrl:</strong> {variant.ImageUrl}
                      </p>

                      {variant.Attributes?.Colors?.Values?.length > 0 && (
                        <p>
                          <strong>Available Colors:</strong>{" "}
                          {variant.Attributes.Colors.Values.map((c) => c.Name).join(", ")}
                        </p>
                      )}

                      {variant.Attributes?.Sizes?.Values?.length > 0 && (
                        <p>
                          <strong>Available Sizes:</strong>{" "}
                          {variant.Attributes.Sizes.Values.map((s) => s.Name).join(", ")}
                        </p>
                      )}

                      {variant.Prices?.length > 0 && (
                        <div style={{ marginTop: "8px" }}>
                          <p>
                            <strong>Pricing Tiers:</strong>
                          </p>
                          <ul style={{ paddingLeft: "16px", marginTop: "4px", marginBottom: 0 }}>
                            {variant.Prices.map((tier, tIdx) => (
                              <li key={tIdx}>
                                <p style={{ margin: 0 }}>
                                  <strong>
                                    Qty {tier.Quantity.From}-{tier.Quantity.To}:
                                  </strong>{" "}
                                  ${tier.Price}
                                  {tier.CurrencyCode ? ` ${tier.CurrencyCode}` : ""}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {variant.PriceIncludes && (
                        <p style={{ marginTop: "8px" }}>
                          <strong>Price Includes:</strong> {variant.PriceIncludes}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Box
        className={`${styles.productImage} product-popup-image-container`}
        sx={{ flexDirection: "column", alignItems: "center", display: "flex", gap: 1 }}
      >
        <Box
          sx={{ position: "relative", width: "100%", display: "inline-block" }}
          onClick={handleImageClick}
          className="product-popup-image-inner-container"
        >
          {heroImageSrc ? (
            <img
              src={heroImageSrc}
              alt="Product"
              style={{ maxWidth: "100%", borderRadius: 8 }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                minHeight: 200,
                borderRadius: 2,
                background: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
              }}
            >
              No image available
            </Box>
          )}

          <EditIcon
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "#ffffffcc",
              borderRadius: "50%",
              padding: "4px",
              fontSize: 20,
              cursor: "pointer",
              color: "#444",
            }}
          />
        </Box>

        {!isPromoStore && itemImageUrls.length >= 2 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhotoLibraryOutlinedIcon />}
            onClick={() => setGalleryOpen(true)}
            sx={{ textTransform: "none", fontWeight: 700, minWidth: 0 }}
          >
            Gallery ({itemImageUrls.length})
          </Button>
        )}

        {isPromoStore && promoStoreGallerySelection.imageUrls.length > 1 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhotoLibraryOutlinedIcon />}
            onClick={() => setPromoStoreGalleryOpen(true)}
            sx={{ textTransform: "none", fontWeight: 700, minWidth: 0 }}
          >
            Gallery ({promoStoreGallerySelection.imageUrls.length})
          </Button>
        )}

        <h4>Resources</h4>

        {asiLiveRawData?.ProductUrl && (
          <a
            href={asiLiveRawData.ProductUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-block",
              padding: "6px 12px",
              backgroundColor: "#4b8544",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              width: "100%",
              fontWeight: "bold",
              textAlign: "center",
              margin: "0",
              textDecoration: "none",
            }}
          >
            View on supplier&apos;s website
          </a>
        )}

        <a
          href="https://app.elitecore.ca/instance/elite-promo-marketing/connect/products"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            padding: "6px 12px",
            backgroundColor: "#444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            width: "100%",
            fontWeight: "bold",
            textAlign: "center",
            margin: "0",
            textDecoration: "none",
          }}
        >
          View on Elite Core
        </a>

        {asiLiveRawData && (
          (asiLiveRawData.LowestPrice ||
            asiLiveRawData.HighestPrice ||
            asiLiveRawData.ArtworkTemplates?.length ||
            asiLiveRawData.Templates?.length) && (
            <Box
              sx={{
                p: 2,
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1,
                bgcolor: "#f9f9f9",
                mt: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <InfoOutlinedIcon sx={{ mr: 1, color: "#c3171d" }} />
                <strong>Quick Product Summary</strong>
              </Box>

              {asiLiveRawData.LowestPrice && (
                <p>
                  <strong>Starting Price:</strong> ${asiLiveRawData.LowestPrice.Price}
                  {asiLiveRawData.LowestPrice.CurrencyCode
                    ? ` ${asiLiveRawData.LowestPrice.CurrencyCode}`
                    : ""}
                </p>
              )}
              {asiLiveRawData.HighestPrice && (
                <p>
                  <strong>Highest Price:</strong> ${asiLiveRawData.HighestPrice.Price}
                  {asiLiveRawData.HighestPrice.CurrencyCode
                    ? ` ${asiLiveRawData.HighestPrice.CurrencyCode}`
                    : ""}
                </p>
              )}

              <p>
                {currencyCode === "USD" && (
                  <Box className="product-country-resource-label">
                    <img src={flagUrl("us")} alt="USA flag" />
                    <span>USA Product <i>(Supplier pricing in USD)</i></span>
                  </Box>
                )}

                {currencyCode === "CAD" && (
                  <Box className="product-country-resource-label">
                    <img src={flagUrl("ca")} alt="Canada flag" />
                    <span>Canadian Product <i>(Supplier pricing in CAD)</i></span>
                  </Box>
                )}
              </p>

              {asiLiveRawData.ArtworkTemplates?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {asiLiveRawData.ArtworkTemplates.map((template, idx) => (
                    <p key={idx}>
                      <strong>Template:</strong>{" "}
                      <a
                        href={template.Value}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#c3171d" }}
                      >
                        {template.DisplayName || template.Value}
                      </a>
                    </p>
                  ))}
                </Box>
              )}

              {asiLiveRawData.Templates?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {asiLiveRawData.Templates.map((template, idx) => (
                    <p key={idx}>
                      <strong>Template:</strong>{" "}
                      <a href={template.Value} target="_blank" rel="noopener noreferrer">
                        {template.DisplayName || template.Value}
                      </a>
                    </p>
                  ))}
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                {asiLiveRawData.Attributes?.Colors?.Values?.length > 0 ? (
                  <p>
                    <strong>Available Colors:</strong>{" "}
                    {asiLiveRawData.Attributes.Colors.Values.map((c) => c.Name).join(", ")}
                  </p>
                ) : (
                  <p><strong>Available Colors:</strong> Not specified by supplier.</p>
                )}

                {asiLiveRawData.Attributes?.Sizes?.Values?.length > 0 ? (
                  <p>
                    <strong>Available Sizes:</strong>{" "}
                    {asiLiveRawData.Attributes.Sizes.Values.map((s) => s.Name).join(", ")}
                  </p>
                ) : (
                  <p><strong>Available Sizes:</strong> Not specified by supplier.</p>
                )}

                {asiLiveRawData.Attributes?.Materials?.Values?.length > 0 ? (
                  <p>
                    <strong>Materials:</strong>{" "}
                    {asiLiveRawData.Attributes.Materials.Values.map((m) => m.Name).join(", ")}
                  </p>
                ) : (
                  <p><strong>Materials:</strong> Not specified by supplier.</p>
                )}
              </Box>

              <Box sx={{ mt: 2 }}>
                {asiLiveRawData.Imprinting?.Methods?.Values?.length > 0 ? (
                  <p>
                    <strong>Imprinting Methods:</strong>{" "}
                    {asiLiveRawData.Imprinting.Methods.Values
                      .map((method) => method.Name)
                      .join(", ")}
                  </p>
                ) : (
                  <p style={{ color: "#b22", fontStyle: "italic" }}>
                    This supplier <strong>({formData.supplier})</strong> doesn&apos;t indicate imprinting availability on premises.
                    If you need imprinting you must use Elite Decorations or another decorator.
                  </p>
                )}
              </Box>
            </Box>
          )
        )}
      </Box>

      <FilesPopup
        handleDone={handleDone}
        open={popupOpen}
        onClose={handleClosePopup}
        onImageSelect={imageChange}
        onImagesSelect={isPromoStore ? handlePromoStoreImagesChange : undefined}
        imageGroups={imageGroups}
        selectedImage={formData?.selectedImage}
        selectedImages={isPromoStore ? promoStoreGallerySelection.imageUrls : undefined}
        customerId={customerId}
        promoStoreProductGalleryMode={isPromoStore}
      />

      {galleryOpen && (
        <Suspense
          fallback={
            <Box
              sx={{
                position: "fixed",
                inset: 0,
                zIndex: 1400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(15, 23, 42, 0.2)",
              }}
            >
              <CircularProgress size={32} />
            </Box>
          }
        >
          <AsiImageGallery
            open={galleryOpen}
            images={itemImageUrls}
            selectedImage={formData?.selectedImage}
            onClose={() => setGalleryOpen(false)}
            onUseImage={imageChange}
          />
        </Suspense>
      )}

      {isPromoStore && promoStoreGallerySelection.imageUrls.length > 1 ? (
        <PromoStoreProductGalleryPreview
          open={promoStoreGalleryOpen}
          images={promoStoreGallerySelection.imageUrls}
          onClose={() => setPromoStoreGalleryOpen(false)}
        />
      ) : null}
    </Box>
  );
};

export default ItemDetails;
